import React from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { getLandingPreviewDevice } from "../previewDevices";

export function PageEditorShell({
  title,
  description,
  device = "desktop",
  saving = false,
  children,
  preview,
}) {
  const previewDevice = getLandingPreviewDevice(device);
  return (
    <Box
      component="main"
      sx={{
        position: "fixed",
        inset: { xs: "136px 0 0", sm: "88px 0 0" },
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: "390px minmax(0,1fr)" },
        bgcolor: "#E9ECF0",
        overflow: "hidden",
      }}
    >
      <Box
        component="aside"
        sx={{
          p: 2,
          overflowY: "auto",
          borderRight: { lg: "1px solid rgba(25,36,47,.12)" },
          bgcolor: "#F8F9FB",
        }}
      >
        <Stack spacing={0.7} sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography variant="h6" sx={{ fontWeight: 950, color: "#19242F" }}>
              {title}
            </Typography>
            {saving ? (
              <Typography variant="caption" sx={{ fontWeight: 850, color: "rgba(25,36,47,.56)" }}>
                Guardando…
              </Typography>
            ) : null}
          </Stack>
          {description ? (
            <Typography variant="body2" sx={{ color: "rgba(25,36,47,.62)" }}>
              {description}
            </Typography>
          ) : null}
        </Stack>
        <Stack spacing={2}>{children}</Stack>
      </Box>

      <Box sx={{ overflow: "auto", p: { xs: 1.25, sm: 2.5 } }}>
        <Box sx={{ minWidth: `${previewDevice.width}px`, width: "fit-content", mx: "auto" }}>
          <Typography
            variant="caption"
            sx={{ display: "block", mb: 1, px: 0.25, color: "rgba(25,36,47,.62)", fontWeight: 900 }}
          >
            {previewDevice.label} · {previewDevice.width}px · vista de configuración
          </Typography>
          <Box
            data-page-editor-preview-device={previewDevice.id}
            sx={{
              width: `${previewDevice.width}px`,
              bgcolor: "#fff",
              borderRadius: previewDevice.id === "mobile" ? 2.5 : 1.5,
              outline: "1px solid rgba(25,36,47,.12)",
              boxShadow: "0 16px 46px rgba(25,36,47,.14)",
              overflow: "hidden",
            }}
          >
            {preview}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export function EditorGroup({ title, description, children }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.6, borderRadius: 2.5 }}>
      <Typography sx={{ fontWeight: 950, color: "#19242F" }}>{title}</Typography>
      {description ? (
        <Typography variant="caption" sx={{ display: "block", mt: 0.35, mb: 1.4, color: "rgba(25,36,47,.58)" }}>
          {description}
        </Typography>
      ) : (
        <Box sx={{ height: 8 }} />
      )}
      <Stack spacing={1.4}>{children}</Stack>
    </Paper>
  );
}

export function TextSetting({ label, value, onChange, multiline = false, minRows = 2, helperText = "" }) {
  return (
    <TextField
      size="small"
      fullWidth
      label={label}
      value={value ?? ""}
      onChange={(event) => onChange(event.target.value)}
      multiline={multiline}
      minRows={multiline ? minRows : undefined}
      helperText={helperText || undefined}
    />
  );
}

export function NumberSetting({ label, value, onChange, min, max, step = 1, helperText = "" }) {
  return (
    <TextField
      size="small"
      fullWidth
      type="number"
      label={label}
      value={Number.isFinite(Number(value)) ? value : ""}
      onChange={(event) => {
        const next = Number(event.target.value);
        if (!Number.isFinite(next)) return;
        onChange(Math.min(max, Math.max(min, next)));
      }}
      inputProps={{ min, max, step }}
      helperText={helperText || undefined}
    />
  );
}

export function ToggleSetting({ label, checked, onChange, helperText = "" }) {
  return (
    <Box>
      <FormControlLabel
        control={<Switch checked={checked !== false} onChange={(event) => onChange(event.target.checked)} />}
        label={label}
        sx={{ m: 0 }}
      />
      {helperText ? (
        <Typography variant="caption" sx={{ display: "block", pl: 6.2, color: "rgba(25,36,47,.56)" }}>
          {helperText}
        </Typography>
      ) : null}
    </Box>
  );
}

export function SelectSetting({ label, value, onChange, options = [], helperText = "" }) {
  return (
    <FormControl size="small" fullWidth>
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {helperText ? (
        <Typography variant="caption" sx={{ mt: 0.5, color: "rgba(25,36,47,.56)" }}>
          {helperText}
        </Typography>
      ) : null}
    </FormControl>
  );
}

export function ColorSetting({ label, value, onChange, helperText = "Vacío = heredar el tema actual" }) {
  return (
    <TextField
      size="small"
      fullWidth
      label={label}
      value={value ?? ""}
      onChange={(event) => onChange(event.target.value)}
      placeholder="#FFFFFF, rgba(...) o transparent"
      helperText={helperText}
      InputProps={{
        startAdornment: value ? (
          <Box
            aria-hidden
            sx={{
              width: 18,
              height: 18,
              mr: 1,
              borderRadius: 0.7,
              bgcolor: value,
              border: "1px solid rgba(25,36,47,.18)",
              flex: "0 0 auto",
            }}
          />
        ) : null,
      }}
    />
  );
}
